import { getTextContent } from '@plato/ui-legacy/lib/rich-text/get-text-content';
import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CrmTaskDTO } from '../../../server/dto/crm-task-dto.ts';
import type { CrmTaskInputDTO } from '../../../server/dto/crm-task-input-dto.ts';
import type { CrmTaskUpdateWithId } from '../../../server/queries/crm-log-interaction-create.ts';
import type { RouterInput } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import type { TaskListItem } from '../log-interaction-tasks-context.ts';

export type LogInteractionFormValues = Omit<
    RouterInput['platoUser']['command']['notes']['create'],
    'noteType'
> & {
    taskUpdates: CrmTaskUpdateWithId[];
    newTasks: CrmTaskInputDTO[];
};

function taskToInput(task: CrmTaskDTO): CrmTaskInputDTO {
    return {
        description: task.descriptionRich,
        assignedToId: task.assignedTo.id,
        // The `?? new Date()` fallback never actually runs, so it can't fabricate a due date.
        // `CrmTaskDTO.dueDate` is typed nullable, but the source column `crm__task.due_date` is
        // NOT NULL and read straight through `prisma.crmTask.findMany` (no left join / coalesce),
        // so `task.dueDate` is always a real `Date` here.
        dueDate: task.dueDate ?? new Date(),
        status: task.status,
        links: task.links,
        attachments: task.attachments,
    };
}

// Stable fingerprint of a task's editable fields, used to tell whether a staged edit still differs from the original.
function taskFingerprint(values: CrmTaskInputDTO): string {
    return JSON.stringify({
        description: getTextContent(values.description),
        assignedToId: values.assignedToId,
        dueDate: values.dueDate,
        status: values.status,
        links: values.links.map((link) => `${link.modelType}:${link.modelId}:${link.title ?? ''}`).toSorted(),
        attachments: (values.attachments ?? []).map((attachment) => attachment.externalUuid).toSorted(),
    });
}

type UseLogInteractionTasksOptions = {
    userId: string;
};

type UseLogInteractionTasksResult = {
    items: TaskListItem[];
    addNewTasks: (tasks: CrmTaskInputDTO[]) => void;
};

export function useLogInteractionTasks(
    form: UseFormReturn<LogInteractionFormValues>,
    { userId }: UseLogInteractionTasksOptions,
): UseLogInteractionTasksResult {
    const { t } = useTranslation('notes');
    const { watch, setValue } = form;

    const customerId = watch('customerId');
    const taskUpdates = watch('taskUpdates');
    const newTasks = watch('newTasks');

    useEffect(() => {
        setValue('taskUpdates', []);
        setValue('newTasks', []);
    }, [customerId, setValue]);

    const upcomingTasksQuery = api.platoUser.query.tasks.getManyTasks.getUpcomingCustomerTasks.useQuery(
        { customerId },
        { enabled: !!customerId },
    );

    const addNewTasks = (tasks: CrmTaskInputDTO[]): void => {
        setValue('newTasks', [...newTasks, ...tasks], { shouldDirty: true });
    };

    const editNewTask = (index: number, task: CrmTaskInputDTO): void => {
        setValue(
            'newTasks',
            newTasks.map((existing, i) => (i === index ? task : existing)),
            { shouldDirty: true },
        );
    };

    const stageTaskUpdate = (task: CrmTaskDTO, next: CrmTaskInputDTO): void => {
        const others = taskUpdates.filter((u) => u.taskId !== task.taskId);
        const revertedToOriginal = taskFingerprint(next) === taskFingerprint(taskToInput(task));
        setValue('taskUpdates', revertedToOriginal ? others : [...others, { ...next, taskId: task.taskId }], {
            shouldDirty: true,
        });
    };

    const existingItems: TaskListItem[] = (upcomingTasksQuery.data ?? []).map((task) => {
        const staged = taskUpdates.find((u) => u.taskId === task.taskId);
        const values: CrmTaskInputDTO = staged ?? taskToInput(task);

        return {
            key: task.taskId,
            values,
            assigneeName:
                values.assignedToId === userId ? t('notes__tasks_assignee_me') : task.assignedTo.fullName,
            pending: staged !== undefined,
            onChange: (next) => {
                stageTaskUpdate(task, next);
            },
        };
    });

    const newItems: TaskListItem[] = newTasks.map((newTask, index) => ({
        key: `pending-${index}`,
        values: newTask,
        assigneeName: newTask.assignedToId === userId ? t('notes__tasks_assignee_me') : undefined,
        pending: true,
        onChange: (next) => {
            editNewTask(index, next);
        },
    }));

    return { items: [...newItems, ...existingItems], addNewTasks };
}
